/**
 * Thin client around @simplewebauthn/browser for passkey enrol / login.
 *
 *  - enrollPasskey(label):   register a new passkey for the currently
 *                            logged-in user (requires an existing auth token
 *                            because /webauthn/register/* are auth-gated).
 *  - loginWithPasskey(id):   authenticate via passkey without OTP. `id`
 *                            (email/phone) is optional — when omitted the
 *                            browser shows ALL passkeys it has stored for
 *                            this RP-ID and lets the user pick one.
 *
 * Returns Promises that resolve with {ok: true, ...} on success and reject
 * with a user-friendly error message on failure. The caller is responsible
 * for routing the user and falling back to the OTP flow.
 */
import { startRegistration, startAuthentication, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import api from "@/lib/api";

export const isPasskeySupported = () => {
  try { return browserSupportsWebAuthn(); } catch { return false; }
};

export async function enrollPasskey(label = "Mein Gerät") {
  if (!isPasskeySupported()) {
    throw new Error("Dieses Gerät / dieser Browser unterstützt keine Passkeys.");
  }
  const optRes = await api.post("/webauthn/register/options");
  const { sid, options } = optRes.data;
  let credential;
  try {
    credential = await startRegistration({ optionsJSON: options });
  } catch (e) {
    if (e?.name === "InvalidStateError") {
      throw new Error("Auf diesem Gerät ist bereits ein Passkey für Sales Pro hinterlegt.");
    }
    if (e?.name === "NotAllowedError") {
      throw new Error("Passkey-Anlage abgebrochen.");
    }
    throw new Error(e?.message || "Passkey-Anlage fehlgeschlagen.");
  }
  const verify = await api.post("/webauthn/register/verify", { sid, credential, label });
  return verify.data;
}

export async function loginWithPasskey(identifier = "") {
  if (!isPasskeySupported()) {
    throw new Error("Dieses Gerät / dieser Browser unterstützt keine Passkeys.");
  }
  const optRes = await api.post("/webauthn/authenticate/options", { identifier });
  const { sid, options } = optRes.data;
  let credential;
  try {
    credential = await startAuthentication({ optionsJSON: options });
  } catch (e) {
    if (e?.name === "NotAllowedError") {
      throw new Error("Passkey-Anmeldung abgebrochen oder kein passender Passkey verfügbar.");
    }
    throw new Error(e?.message || "Passkey-Anmeldung fehlgeschlagen.");
  }
  const verify = await api.post("/webauthn/authenticate/verify", { sid, credential });
  return verify.data;
}

export async function listPasskeys() {
  const res = await api.get("/webauthn/credentials");
  return res.data.credentials || [];
}

export async function deletePasskey(id) {
  const res = await api.delete(`/webauthn/credentials/${id}`);
  return res.data;
}
